import { RegisteredComponent } from "@builder.io/sdk-react";
import { imageLoader } from "@/helpers/imageLoader";
import { getDimensions } from "@/helpers/getDimensions";
import type { ImageBlock } from "@/libs/builder/blocks/ImageBlock";
import { BuilderBlockProps } from "@/types";

export const imageBlockConfig: Omit<RegisteredComponent, "component"> = {
  name: "Image",
  override: true,
  docsLink: "https://www.fastly.com/documentation/reference/io/",
  noWrap: true,
  defaultStyles: {
    alignSelf: "center",
    marginTop: "0px",
    width: "",
  },
  inputs: [
    {
      name: "image",
      type: "AprimoAssetSelector",
      options: {
        searchingExpression:
          'Contentstatus = "Released" AND ContentType = "Image" AND Classification.NamePath = "/DAM/Audience/External" AND Classification.NamePath = "/DAM/Status/Available"',
      },
      required: true,
      localized: true,
      helperText:
        "Select the image from the DAM (Aprimo). Only images which are permitted to use on the website are selectable; therefore, archived, draft or internal assets can't be selected.",
    },
    {
      name: "alt",
      type: "text",
      friendlyName: "Alternate text",
      helperText:
        "Please provide text which describes the image well. This is mainly used for accessibility. Without an alternate text, the image won't render!",
      required: true,
      localized: true,
    },
    {
      name: "priority",
      type: "boolean",
      helperText:
        "Enable if the image needs to be preloaded (usually recommended when the image is above the fold or the image is very large).",
      defaultValue: false,
      localized: false,
    },
    {
      name: "width",
      type: "number",
      helperText:
        "Leave the value empty if the original width should be used instead.",
      localized: false,
    },
    {
      name: "height",
      type: "number",
      helperText:
        "Leave the value empty if the original height should be used instead.",
      localized: false,
    },
    {
      name: "upscale",
      type: "boolean",
      defaultValue: false,
      friendlyName: "Enable upscale",
      helperText:
        "Allow images being resized such that the output image's dimensions are larger than the source image. If the output image's dimensions are larger than the source image, the image may seem blurry.",
      localized: false,
    },
    {
      name: "fit",
      type: "enum",
      helperText: "Prevent the image from looking squashed or deformed.",
      enum: [
        {
          label: "cover",
          value: "cover",
        },
        {
          label: "bounds",
          value: "bounds",
        },
        {
          label: "crop",
          value: "crop",
        },
      ],
      localized: false,
      showIf: (options) =>
        Number.isSafeInteger(options?.get("width")) &&
        Number.isSafeInteger(options?.get("height")),
    },
    {
      name: "quality",
      type: "enum",
      enum: [
        {
          label: "25",
          value: 25,
        },
        {
          label: "50",
          value: 50,
        },

        {
          label: "75",
          value: 75,
        },

        {
          label: "100",
          value: 100,
        },
      ],
      helperText:
        "The greater the number, the better the quality of the image, however the performance will be worse.",
    },
    {
      name: "crop",
      type: "object",
      advanced: true,
      subFields: [
        {
          type: "enum",
          name: "type",
          helperText:
            "There are two types of defining cropping: either by defining the exact size in pixels of the resulting cropped image or by defining the desired aspact ratio of the cropped image.",
          enum: [
            {
              label: "ratio",
              value: "ratio",
            },
            {
              label: "pixels",
              value: "pixels",
            },
          ],
          localized: false,
        },
        {
          type: "number",
          name: "widthPixels",
          friendlyName: "Width in pixels",
          required: true,
          localized: false,
          showIf: (options) => options?.get("type") === "pixels",
        },
        {
          type: "number",
          name: "heightPixels",
          friendlyName: "Height in pixels",
          required: true,
          localized: false,
          showIf: (options) => options?.get("type") === "pixels",
        },
        {
          type: "number",
          name: "widthRatio",
          friendlyName: "Width ratio",
          required: true,
          localized: false,
          showIf: (options) => options?.get("type") === "ratio",
        },
        {
          type: "number",
          name: "heightRatio",
          friendlyName: "Height ratio",
          required: true,
          localized: false,
          showIf: (options) => options?.get("type") === "ratio",
        },
        {
          type: "enum",
          name: "cropStartPosition",
          friendlyName: "Crop start position",
          helperText:
            "Define the crop starting position either by providing the exact pixels or percentage.",
          enum: [
            {
              label: "in pixels",
              value: "pixels",
            },
            {
              label: "in percentage",
              value: "percentage",
            },
          ],
          localized: false,
          showIf: (options) =>
            options?.get("widthPixels") ||
            options?.get("heightPixels") ||
            options?.get("widthRatio") ||
            options?.get("heightRatio"),
        },
        {
          type: "number",
          name: "x",
          friendlyName: "X coordinate (in pixels)",
          helperText:
            "Define the x coordinate in pixels where the crop starts from the top left corner.",
          defaultValue: 0,
          required: true,
          localized: false,
          showIf: (options) => options?.get("cropStartPosition") === "pixels",
        },
        {
          type: "number",
          name: "y",
          friendlyName: "Y coordinate (in pixels)",
          helperText:
            "Define the y coordinate in pixels where the crop starts from the top left corner.",
          defaultValue: 0,
          required: true,
          localized: false,
          showIf: (options) => options?.get("cropStartPosition") === "pixels",
        },
        {
          type: "number",
          name: "xOffset",
          friendlyName: "X offset (in percentage)",
          helperText:
            "Define the x offset in percentage where the crop starts from the top left corner.",
          defaultValue: 0,
          required: true,
          localized: false,
          showIf: (options) =>
            options?.get("cropStartPosition") === "percentage",
        },
        {
          type: "number",
          name: "yOffset",
          friendlyName: "Y offset (in percentage)",
          helperText:
            "Define the y offset in percentage where the crop starts from the top left corner.",
          defaultValue: 0,
          required: true,
          localized: false,
          showIf: (options) =>
            options?.get("cropStartPosition") === "percentage",
        },
      ],
      localized: false,
    },
    {
      name: "trim",
      type: "object",
      advanced: true,
      subFields: [
        {
          name: "top",
          type: "number",
          friendlyName: "Top (in pixels or percentage)",
          required: true,
          helperText:
            "Provide a number in pixels or percentages (from 0 to 1).",
        },
        {
          name: "right",
          type: "number",
          friendlyName: "Right (in pixels or percentage)",
          required: true,
          helperText:
            "Provide a number in pixels or percentages (from 0 to 1).",
        },
        {
          name: "bottom",
          type: "number",
          friendlyName: "Bottom (in pixels or percentage)",
          required: true,
          helperText:
            "Provide a number in pixels or percentages (from 0 to 1).",
        },
        {
          name: "left",
          type: "number",
          friendlyName: "Left (in pixels or percentage)",
          required: true,
          helperText:
            "Provide a number in pixels or percentages (from 0 to 1).",
        },
      ],
    },
    {
      name: "orient",
      type: "enum",
      advanced: true,
      friendlyName: "Orientation",
      enum: [
        {
          label: "rotate right",
          value: "r",
        },
        {
          label: "rotate left",
          value: "l",
        },
        {
          label: "flip the image horizontally",
          value: "h",
        },
        {
          label: "flip the image vertically",
          value: "v",
        },
        {
          label: "flip the image both horizontally and vertically",
          value: "vh",
        },
        {
          label: "flip the image horizontally, then rotate the image left",
          value: "rv",
        },
        {
          label: "flip the image horizontally, then rotate the image right",
          value: "lv",
        },
      ],
      localized: false,
    },
    {
      name: "blur",
      type: "number",
      advanced: true,
      min: 0.5,
      max: 1000.0,
      step: 0.1,
      defaultValue: 0.5,
      helperText:
        "Provide a number between 0.5 to 1000. The default value is 0.5.",
      localized: false,
    },
    {
      name: "brightness",
      type: "number",
      advanced: true,
      min: -100,
      max: 100,
      defaultValue: 0,
      helperText:
        "Provide a number between -100 to 100. The default value is 0.",
      localized: false,
    },
    {
      name: "contrast",
      type: "number",
      advanced: true,
      min: -100,
      max: 100,
      defaultValue: 0,
      helperText:
        "Provide a number between -100 to 100. The default value is 0.",
      localized: false,
    },
    {
      name: "saturation",
      type: "number",
      advanced: true,
      min: -100,
      max: 100,
      defaultValue: 0,
      helperText:
        "Provide a number between -100 to 100. The default value is 0.",
      localized: false,
    },
  ],
};

export const remapProps = async (
  props: Omit<ImageBlock, keyof BuilderBlockProps>,
) => {
  if (!props.image) {
    return {
      intrinsicWidth: 0,
      intrinsicHeight: 0,
    };
  }

  const url = new URL(
    imageLoader(props)({
      src: props.image,
      quality: props.quality,
      width: props.width!,
    }),
  );
  return getDimensions(url.toString());
};
